import api from '@/api/axios';
import { ENDPOINTS } from '@/api/endpoints';
import {
  Workspace,
  WorkspaceCreateData,
  WorkspaceUpdateData,
  WorkspaceMember,
  WorkspaceMemberAddData,
  WorkspaceMemberUpdateData,
  WorkspaceInvitation,
  WorkspaceInviteData,
  WorkspaceDashboard,
  WorkspaceProject,
} from '@/types';

/**
 * Workspace API service
 * Handles all workspace-related HTTP requests
 */

// ========== Workspace CRUD ==========

/**
 * Get all workspaces for current user
 */
export const getWorkspaces = async (): Promise<Workspace[]> => {
  const response = await api.get<{ workspaces: Workspace[] }>(ENDPOINTS.WORKSPACES.LIST);
  return response.data.workspaces || [];
};

/**
 * Get workspace by ID
 */
export const getWorkspaceById = async (workspaceId: string): Promise<Workspace> => {
  const response = await api.get<Workspace>(ENDPOINTS.WORKSPACES.DETAILS(workspaceId));
  return response.data;
};

/**
 * Get workspace by slug
 */
export const getWorkspaceBySlug = async (slug: string): Promise<Workspace> => {
  const response = await api.get<Workspace>(ENDPOINTS.WORKSPACES.BY_SLUG(slug));
  return response.data;
};

/**
 * Create new workspace
 */
export const createWorkspace = async (data: WorkspaceCreateData): Promise<Workspace> => {
  const response = await api.post<Workspace>(ENDPOINTS.WORKSPACES.BASE, data);
  return response.data;
};

/**
 * Update workspace
 */
export const updateWorkspace = async (
  workspaceId: string,
  data: WorkspaceUpdateData
): Promise<{ status: string; message: string }> => {
  const response = await api.put<{ status: string; message: string }>(
    ENDPOINTS.WORKSPACES.DETAILS(workspaceId),
    data
  );
  return response.data;
};

/**
 * Delete workspace
 */
export const deleteWorkspace = async (workspaceId: string): Promise<void> => {
  await api.delete(ENDPOINTS.WORKSPACES.DETAILS(workspaceId));
};

// ========== Workspace Members ==========

/**
 * Get workspace members
 */
export const getWorkspaceMembers = async (workspaceId: string): Promise<WorkspaceMember[]> => {
  const response = await api.get<{ members: WorkspaceMember[] }>(
    ENDPOINTS.WORKSPACES.MEMBERS(workspaceId)
  );
  return response.data.members || [];
};

/**
 * Add member to workspace
 */
export const addWorkspaceMember = async (
  workspaceId: string,
  data: WorkspaceMemberAddData
): Promise<{ status: string; message: string }> => {
  const response = await api.post<{ status: string; message: string }>(
    ENDPOINTS.WORKSPACES.MEMBERS(workspaceId),
    data
  );
  return response.data;
};

/**
 * Update member role
 */
export const updateWorkspaceMember = async (
  workspaceId: string,
  userId: string,
  data: WorkspaceMemberUpdateData
): Promise<{ status: string; message: string }> => {
  const response = await api.put<{ status: string; message: string }>(
    ENDPOINTS.WORKSPACES.MEMBER(workspaceId, userId),
    data
  );
  return response.data;
};

/**
 * Remove member from workspace
 */
export const removeWorkspaceMember = async (
  workspaceId: string,
  userId: string
): Promise<void> => {
  await api.delete(ENDPOINTS.WORKSPACES.MEMBER(workspaceId, userId));
};

/**
 * Leave workspace
 */
export const leaveWorkspace = async (workspaceId: string): Promise<void> => {
  await api.delete(ENDPOINTS.WORKSPACES.LEAVE(workspaceId));
};

// ========== Workspace Invitations ==========

/**
 * Invite user to workspace
 */
export const inviteUserToWorkspace = async (
  workspaceId: string,
  data: WorkspaceInviteData
): Promise<WorkspaceInvitation> => {
  const response = await api.post<WorkspaceInvitation>(
    ENDPOINTS.WORKSPACES.INVITATIONS.LIST(workspaceId),
    data
  );
  return response.data;
};

/**
 * Get workspace invitations (admin view)
 */
export const getWorkspaceInvitations = async (
  workspaceId: string
): Promise<WorkspaceInvitation[]> => {
  const response = await api.get<{ invitations: WorkspaceInvitation[] }>(
    ENDPOINTS.WORKSPACES.INVITATIONS.LIST(workspaceId)
  );
  return response.data.invitations || [];
};

/**
 * Get received workspace invitations (user view)
 */
export const getReceivedWorkspaceInvitations = async (): Promise<WorkspaceInvitation[]> => {
  const response = await api.get<{ invitations: WorkspaceInvitation[] }>(
    ENDPOINTS.WORKSPACES.INVITATIONS.RECEIVED
  );
  return response.data.invitations || [];
};

/**
 * Accept workspace invitation
 */
export const acceptWorkspaceInvitation = async (
  invitationId: string
): Promise<{ status: string; message: string }> => {
  const response = await api.put<{ status: string; message: string }>(
    ENDPOINTS.WORKSPACES.INVITATIONS.ACCEPT(invitationId),
    {}
  );
  return response.data;
};

/**
 * Reject workspace invitation
 */
export const rejectWorkspaceInvitation = async (
  invitationId: string
): Promise<{ status: string; message: string }> => {
  const response = await api.put<{ status: string; message: string }>(
    ENDPOINTS.WORKSPACES.INVITATIONS.REJECT(invitationId),
    {}
  );
  return response.data;
};

/**
 * Cancel workspace invitation
 */
export const cancelWorkspaceInvitation = async (invitationId: string): Promise<void> => {
  await api.delete(ENDPOINTS.WORKSPACES.INVITATIONS.CANCEL(invitationId));
};

/**
 * Accept workspace invitation by encrypted code (from email link)
 */
export const acceptWorkspaceInvitationByCode = async (
  code: string
): Promise<{
  success: boolean;
  workspaceId: string;
  workspaceName: string;
  workspaceSlug: string;
}> => {
  const response = await api.post(ENDPOINTS.WORKSPACES.INVITATIONS.ACCEPT_BY_CODE, { code });
  return response.data;
};

/**
 * Reject workspace invitation by encrypted code (from email link)
 */
export const rejectWorkspaceInvitationByCode = async (
  code: string
): Promise<{ status: string; message: string }> => {
  const response = await api.post<{ status: string; message: string }>(
    ENDPOINTS.WORKSPACES.INVITATIONS.REJECT_BY_CODE,
    { code }
  );
  return response.data;
};

// ========== Workspace Projects & Dashboard ==========

export interface CreateProjectData {
  projectName: string;
  projectDescription?: string;
  projectKey?: string;
}

/**
 * Get workspace projects
 */
export const getWorkspaceProjects = async (workspaceId: string): Promise<WorkspaceProject[]> => {
  const response = await api.get<{ projects: WorkspaceProject[] }>(
    ENDPOINTS.WORKSPACES.PROJECTS(workspaceId)
  );
  return response.data.projects || [];
};

/**
 * Create a new project in workspace
 */
export const createWorkspaceProject = async (
  workspaceId: string,
  data: CreateProjectData
): Promise<WorkspaceProject> => {
  const response = await api.post<WorkspaceProject>(
    ENDPOINTS.WORKSPACES.PROJECTS(workspaceId),
    data
  );
  return response.data;
};

/**
 * Get workspace dashboard
 */
export const getWorkspaceDashboard = async (workspaceId: string): Promise<WorkspaceDashboard> => {
  const response = await api.get<WorkspaceDashboard>(
    ENDPOINTS.WORKSPACES.DASHBOARD(workspaceId)
  );
  return response.data;
};

export const workspaceApi = {
  getWorkspaces,
  getWorkspaceById,
  getWorkspaceBySlug,
  createWorkspace,
  updateWorkspace,
  deleteWorkspace,
  getWorkspaceMembers,
  addWorkspaceMember,
  updateWorkspaceMember,
  removeWorkspaceMember,
  leaveWorkspace,
  inviteUserToWorkspace,
  getWorkspaceInvitations,
  getReceivedWorkspaceInvitations,
  acceptWorkspaceInvitation,
  rejectWorkspaceInvitation,
  cancelWorkspaceInvitation,
  acceptWorkspaceInvitationByCode,
  rejectWorkspaceInvitationByCode,
  getWorkspaceProjects,
  createWorkspaceProject,
  getWorkspaceDashboard,
};

export default workspaceApi;
